<template>
  <div>
    <kt-datatable
      :model="datatableModel"
      :data="rows"
      :filter="datatableFilter"
      :meta="{ tableClass: 'table', rowClass: getRowClass }"
      :update-function="handleDatatableUpdate"
      :hide-search="false"
      @cell-click="handleCellClick"
      storage-key="procedure-checklist-list"
    >
      <template #filters>
        <div class="col" v-if="!props.hideFilters">
          <div class="row">
            <div class="col-auto" v-if="!props.companyId">
              <client-multi-check
                label="Customer"
                :model-value="currentFilter.companyId"
                @update:model-value="(value: number[]) => setFilter({ companyId: value })"
              />
            </div>
            <div class="col-auto" v-if="!props.category">
              <form-category-multi-check
                label="Category"
                :model-value="currentFilter.category"
                @update:model-value="(value: string[]) => setFilter({ category: value })"
              />
            </div>
            <div class="col-auto" v-if="!props.status">
              <form-submission-status-multi-check
                label="Status"
                :model-value="currentFilter.status"
                @update:model-value="(value: number[]) => setFilter({ status: value })"
              />
            </div>
            <div class="col-auto" v-if="!props.technicianId">
              <tech-multi-check
                label="Technician"
                :model-value="currentFilter.technician"
                @update:model-value="(value: number[]) => setFilter({ technician: value })"
              />
            </div>
          </div>
        </div>
      </template>

      <template #after-search>
        <div class="btn-group">
          <button @click="refresh()" class="btn btn-sm btn-outline-secondary me-2">
            Refresh <i class="fa fa-sync"></i>
          </button>
        </div>
      </template>

      <template #view_string="scope">
        <span v-if="scope.column.name === 'DocumentName'">
          {{ scope.row.DocumentName || 'N/A' }}
        </span>
        <span v-else-if="scope.column.name === 'Customer'">
          {{ getCustomerName(scope.row.CompanyId) }}
        </span>
        <span v-else-if="scope.column.name === 'Category'">
          {{ scope.row.Template?.Category || 'N/A' }}
        </span>
        <span v-else-if="scope.column.name === 'TemplateName'">
          {{ scope.row.Template?.Title || 'N/A' }}
        </span>
        <span v-else-if="scope.column.name === 'Status'">
          <span :class="getStatusBadgeClass(scope.row.Status)">
            {{ getStatusLabel(scope.row.Status) }}
          </span>
        </span>
        <span v-else-if="scope.column.name === 'Technician'">
          {{ getTechnicianName(scope.row.SubmittedBy) }}
        </span>
        <span v-else-if="scope.column.name === 'SubmitterName'">
          {{ scope.row.SubmitterName || 'N/A' }}
        </span>
        <span v-else>
          {{ scope.value }}
        </span>
      </template>

      <template #view_date="scope">
        {{ formatDate(scope.value) }}
      </template>

      <template #view_actions="scope">
        <div class="d-flex gap-1">
          <button
            v-if="!props.disableActions"
            class="btn btn-sm btn-outline-primary"
            title="Open"
            @click.stop="openDocument(scope.row.Id!)"
          >
            <i class="fa fa-eye"></i>
          </button>
        </div>
      </template>
    </kt-datatable>

    <kt-modal
      :visible="showView"
      :title="viewing?.DocumentName || 'Document'"
      size="modal-xl"
      @dismiss="closeView"
    >
      <template #body>
        <div v-if="viewing">
          <div class="row mb-3">
            <div class="col-md-3">
              <label class="form-label">Customer</label>
              <div>{{ getCustomerName(viewing.CompanyId) }}</div>
            </div>
            <div class="col-md-3">
              <label class="form-label">Template</label>
              <div>{{ viewing.Template?.Title || 'N/A' }}</div>
            </div>
            <div class="col-md-3">
              <label class="form-label">Category</label>
              <div>{{ viewing.Template?.Category || 'N/A' }}</div>
            </div>
            <div class="col-md-3">
              <label class="form-label">Status</label>
              <div>
                <span :class="getStatusBadgeClass(viewing.Status)">
                  {{ getStatusLabel(viewing.Status) }}
                </span>
              </div>
            </div>
          </div>
          <div class="row mb-3">
            <div class="col-md-3">
              <label class="form-label">Technician</label>
              <div>{{ getTechnicianName(viewing.SubmittedBy) }}</div>
            </div>
            <div class="col-md-3">
              <label class="form-label">Created</label>
              <div>{{ formatDate(viewing.DateCreated) }}</div>
            </div>
            <div class="col-md-3">
              <label class="form-label">Submitted</label>
              <div>{{ formatDate(viewing.DateSubmitted) }}</div>
            </div>
          </div>
          <form-renderer
            v-if="viewStructure"
            :key="viewing.Id"
            :form-structure="viewStructure"
            :initial-answers="viewAnswers"
            :read-only="true"
          />
        </div>
      </template>
      <template #footer>
        <button class="btn btn-secondary" @click="closeView">Close</button>
      </template>
    </kt-modal>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue';
import { storeToRefs } from 'pinia';
import Api, { ApiResponse, FormSubmissionModel, FormSubmissionFilter } from '@/services/ApiService';
import KtDatatable from '@/components/UI/KtDatatable.vue';
import KtModal from '@/components/UI/KtModal.vue';
import FormRenderer from '@/components/FormBuilder/FormRenderer.vue';
import ClientMultiCheck from '@/components/Clients/ClientMultiCheck.vue';
import TechMultiCheck from '@/components/Techs/TechMultiCheck.vue';
import FormCategoryMultiCheck from '@/components/Forms/FormCategoryMultiCheck.vue';
import FormSubmissionStatusMultiCheck from '@/components/Forms/FormSubmissionStatusMultiCheck.vue';
import { useClientStore } from '@/stores/ClientStore';
import { useTechStore } from '@/stores/TechStore';
import { normalizeFormStructure } from '@/components/utils/form-helpers';
import type { FormStructure, FormAnswer } from '@/components/types/form-builder';

const props = defineProps({
  companyId: { type: [Number, String], default: null },
  technicianId: { type: [Number, String], default: null },
  category: { type: String, default: null },
  status: { type: [Number, String], default: null },
  templateId: { type: String, default: null },
  embedded: { type: Boolean, default: false },
  hideFilters: { type: Boolean, default: false },
  disableActions: { type: Boolean, default: false }
});

const emit = defineEmits(['edit', 'new', 'count-loaded']);

// Store access
const clientStore = useClientStore();
const { list: clientList } = storeToRefs(clientStore);

const techStore = useTechStore();
const { list: techList } = storeToRefs(techStore);

clientStore.load();
techStore.load();

// Data
const rows = ref<FormSubmissionModel[]>([]);
const total = ref(0);

// View modal
const showView = ref(false);
const viewing = ref<FormSubmissionModel | null>(null);
const viewStructure = ref<FormStructure | null>(null);
const viewAnswers = ref<FormAnswer>({});

interface ProcedureChecklistFilter {
  companyId: number[];
  category: string[];
  status: number[];
  technician: number[];
  templateId: string[];
  searchTerm: string;
  page: number;
  size: number;
  sort: Array<Record<string, 'ASC' | 'DESC'>>;
}

const getInitialFilter = (): ProcedureChecklistFilter => ({
  companyId: props.companyId ? [Number(props.companyId)] : [],
  category: props.category ? [props.category] : [],
  status: props.status !== null ? [Number(props.status)] : [],
  technician: props.technicianId ? [Number(props.technicianId)] : [],
  templateId: props.templateId ? [props.templateId] : [],
  searchTerm: '',
  page: 0,
  size: 25,
  sort: []
});

const currentFilter = ref<ProcedureChecklistFilter>(getInitialFilter());

const datatableFilter = computed(() => ({
  page: currentFilter.value.page,
  size: currentFilter.value.size,
  total: total.value,
  search: currentFilter.value.searchTerm,
  sort: currentFilter.value.sort
}));

const datatableModel = [
  { name: 'DocumentName', label: 'Document Name', type: 'string', display: true, sortable: true },
  { name: 'Customer', label: 'Customer', type: 'string', display: true },
  { name: 'Category', label: 'Category', type: 'string', display: true },
  { name: 'TemplateName', label: 'Template', type: 'string', display: true },
  { name: 'Status', label: 'Status', type: 'string', display: true },
  { name: 'Technician', label: 'Technician', type: 'string', display: true },
  { name: 'DateCreated', label: 'Created', type: 'date', display: true, sortable: true },
  { name: 'DateSubmitted', label: 'Submitted', type: 'date', display: true, sortable: true },
  { name: 'Actions', label: '', type: 'actions', display: true, width: 60 }
];

const buildOrderBy = (sort: Array<Record<string, 'ASC' | 'DESC'>>): string | undefined => {
  if (!sort || !sort.length) return undefined;
  return sort.map((s) => {
    const [col, dir] = Object.entries(s)[0];
    return dir === 'DESC' ? `-${col}` : col;
  }).join(',');
};

const loadData = async () => {
  try {
    const params: FormSubmissionFilter = {
      Page: currentFilter.value.page,
      Size: currentFilter.value.size,
      SearchTerm: currentFilter.value.searchTerm || undefined,
      CompanyId: currentFilter.value.companyId.length ? currentFilter.value.companyId : undefined,
      Category: currentFilter.value.category.length ? currentFilter.value.category : undefined,
      Status: currentFilter.value.status.length ? currentFilter.value.status : undefined,
      SubmittedBy: currentFilter.value.technician.length ? currentFilter.value.technician : undefined,
      TemplateId: currentFilter.value.templateId.length ? currentFilter.value.templateId : undefined,
      IncludeTemplate: true,
      OrderBy: buildOrderBy(currentFilter.value.sort)
    };

    const response: ApiResponse<FormSubmissionModel[]> = await Api.loadFormSubmissions(params);
    rows.value = response.Data || [];
    total.value = response.Meta?.Total || 0;
    emit('count-loaded', total.value);
  } catch (error) {
    console.error('Failed to load procedures / checklists:', error);
    rows.value = [];
    total.value = 0;
  }
};

const handleDatatableUpdate = (filterUpdate: any) => {
  if (filterUpdate.page !== undefined) currentFilter.value.page = filterUpdate.page;
  if (filterUpdate.size !== undefined) currentFilter.value.size = filterUpdate.size;
  if (filterUpdate.search !== undefined) currentFilter.value.searchTerm = filterUpdate.search;
  if (filterUpdate.sort !== undefined) currentFilter.value.sort = filterUpdate.sort || [];
  loadData();
};

const setFilter = (updates: Partial<ProcedureChecklistFilter>) => {
  currentFilter.value = { ...currentFilter.value, ...updates, page: 0 };
  loadData();
};

const refresh = () => {
  loadData();
};

const newRecord = () => {
  if (props.embedded) {
    emit('new');
  } else {
    // Not implemented for the standalone list in the first version
    console.warn('New procedure/checklist not implemented yet');
  }
};

const openDocument = async (id: string) => {
  try {
    const document = await Api.getJobDocument(id, true);
    viewing.value = document;
    viewStructure.value = normalizeFormStructure(JSON.parse(document.TemplateSchemaSnapshot || document.Template?.FormSchema || '{}'));
    viewAnswers.value = JSON.parse(document.FormData || '{}');
    showView.value = true;
  } catch (error) {
    console.error('Failed to open document:', error);
  }
};

const closeView = () => {
  showView.value = false;
  viewing.value = null;
  viewStructure.value = null;
  viewAnswers.value = {};
};

const handleCellClick = (row: FormSubmissionModel, column: any, value: any, rowIndex: number) => {
  if (column.name === 'Actions' || column.name === 'Select') return;
  if (props.embedded) {
    emit('edit', row.Id);
  } else if (!props.disableActions) {
    openDocument(row.Id!);
  }
};

const getCustomerName = (customerNumber?: number) => {
  if (!customerNumber) return 'N/A';
  const client = clientList.value.find(c => c.CustomerNumber === customerNumber);
  return client?.Company || `Customer #${customerNumber}`;
};

const getTechnicianName = (techId?: number) => {
  if (!techId) return 'N/A';
  const tech = techList.value[techId];
  return tech?.TechnicianName || `User #${techId}`;
};

const getStatusLabel = (status: number) => {
  if (status === 0) return 'Draft';
  const parts: string[] = [];
  if (status & 1) parts.push('Submitted');
  if (status & 2) parts.push('Approved');
  if (status & 4) parts.push('Rejected');
  return parts.join(' / ') || 'Unknown';
};

const getStatusBadgeClass = (status: number) => {
  if (status === 0) return 'badge bg-secondary';
  if (status & 4) return 'badge bg-danger';
  if (status & 2) return 'badge bg-success';
  if (status & 1) return 'badge bg-primary';
  return 'badge bg-secondary';
};

const getRowClass = (row: FormSubmissionModel) => {
  if (row.Status & 4) return 'table-danger';
  if (row.Status & 2) return 'table-success';
  if (row.Status & 1) return 'table-info';
  return '';
};

const formatDate = (value?: string) => {
  if (!value) return '';
  try {
    const d = new Date(value);
    return d.toLocaleDateString() + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } catch {
    return value;
  }
};

watch(() => props.companyId, () => { currentFilter.value.companyId = props.companyId ? [Number(props.companyId)] : []; loadData(); });
watch(() => props.technicianId, () => { currentFilter.value.technician = props.technicianId ? [Number(props.technicianId)] : []; loadData(); });
watch(() => props.category, () => { currentFilter.value.category = props.category ? [props.category] : []; loadData(); });
watch(() => props.status, () => { currentFilter.value.status = props.status !== null ? [Number(props.status)] : []; loadData(); });
watch(() => props.templateId, () => { currentFilter.value.templateId = props.templateId ? [props.templateId] : []; loadData(); });

onMounted(() => {
  loadData();
});
</script>

<style scoped>
.form-label {
  font-weight: 600;
  font-size: 0.85rem;
  margin-bottom: 0.25rem;
}
</style>
